// Lageret for NJORD — postens situasjonsbilde i minnet.
//
// MEKANIKKEN BOR IKKE HER. Den uforanderlige tilstandsmaskinen (dedup, stabil
// sortering, «kast aldri en observasjon fra en ukjent sensor», borte-logg,
// «broen overskriver ikke operatørens flagg») ligger i `@skade/sensorflate`,
// fordi den er identisk for hver sensorflate — se toppkommentaren i pakkens
// `index.ts`. Denne fila er NJORDs bestilling: postens fire sensorarter, deres
// rekkefølge, og hva et nøytralt operatørflagg er.
//
// Alt som eksporteres herfra har samme navn og samme form som før uttrekket, så
// resten av appen merker ingenting.
import { lagLagermotor, type Flatetilstand } from '@skade/sensorflate';
import type {
  KopMelding, KopPost, KopSensor, Kystkontakt, Observasjon, Operatorflagg, Sensorart,
} from '../api/typer.js';

/**
 * Rekkefølgen sensorstolpen ALLTID vises i. Fast og ikke sortert på status:
 * operatøren skal kunne strekke hånda mot «radaren er øverst» uten å lese.
 * Sorterer man på status, flytter radene seg når noe går galt — altså nøyaktig
 * når stabilitet betyr mest.
 *
 * Dette er et OPERATIVT valg for en kystobservasjonspost, ikke en teknisk
 * detalj, og det er grunnen til at lista ligger i appen og ikke i pakken: ULL
 * har seks andre arter i en annen rekkefølge, og begge må kunne ha rett.
 */
export const SENSORREKKEFOLGE = ['radar', 'rf', 'hydrofon', 'kamera'] as const;

export type Lagertilstand = Flatetilstand<KopSensor, KopPost, Kystkontakt>;

const motor = lagLagermotor<Sensorart, KopSensor, KopPost, Observasjon, Kystkontakt>({
  rekkefolge: SENSORREKKEFOLGE,
  noytraltFlagg: 'ingen' satisfies Operatorflagg,
  /**
   * En sensor vi bare kjenner gjennom en observasjon får `tilstand: 'ukjent'` og
   * en merknad som sier hvorfor — det er en driftsfeil operatøren skal SE, ikke
   * en vi skal skjule ved å finne på et navn og påstå at alt er i orden.
   *
   * MERK arten: `'radar'`. `Sensorart` er en lukket union uten «annet»-verdi (se
   * `api/typer.ts`), så noe MÅ velges, og valget står her — synlig — i stedet for
   * gjemt inne i en delt pakke. Oppførselen er uendret fra før uttrekket.
   */
  ukjentSensor: (sensorId) => ({
    id: sensorId,
    art: 'radar',
    navn: sensorId,
    postId: '',
    tilstand: 'ukjent',
    merknad: 'Sensoren er ikke annonsert av broen — kjenner bare målingene',
  }),
});

export function tomtLager(): Lagertilstand {
  return motor.tomtLager();
}

/** Bruk én melding på tilstanden. Returnerer NY tilstand (aldri mutert inn). */
export function bruk(t: Lagertilstand, m: KopMelding): Lagertilstand {
  return motor.bruk(t, stemplet(m));
}

/**
 * ØVINGSSTEMPELET FRA KONVOLUTTEN NED PÅ ENTITETEN.
 *
 * Øvingskilden stempler konvolutten (`lagOvelseskilde` — den ene utgangen), men
 * det er ENTITETENE som overlever inn i lageret og videre ut i kortet, lista,
 * kartet og en eventuell `Track`. Uten denne funksjonen ville flagget stoppet i
 * dørkarmen: meldingen var merket, kontakten ikke, og et kontaktkort som vises
 * alene hadde ingen måte å vite at det var oppdiktet.
 *
 * Observasjonene merkes også, én for én. Det ser overdrevet ut helt til man
 * husker at en observasjon vises som en EGEN rad i kontaktkortet, og at
 * `sensordekning` plukker dem ut av kontakten sin.
 *
 * MERK at vi bare STEMPLER — vi fjerner aldri et flagg. En ekte bro kan ikke
 * sette det (`tolkMelding` leser det ikke fra wire), så et flagg som er der,
 * skal bli der.
 */
function stemplet(m: KopMelding): KopMelding {
  if (m.ovelse !== true) return m;
  switch (m.type) {
    case 'post':
      return { ...m, post: { ...m.post, ovelse: true } };
    case 'sensor':
      return { ...m, sensor: { ...m.sensor, ovelse: true } };
    case 'observasjon':
      return { ...m, observasjon: { ...m.observasjon, ovelse: true } };
    case 'kontakt':
      return {
        ...m,
        kontakt: {
          ...m.kontakt,
          ovelse: true,
          observasjoner: m.kontakt.observasjoner.map((o) => ({ ...o, ovelse: true as const })),
        },
      };
    default:
      // `kontakt-borte`/`sensor-borte` bærer ingen entitet — bare en id og en
      // årsak. Det er ingenting å stemple, og borte-loggen viser ikke data.
      return m;
  }
}

/** Operatørens flagg — den ene endringen som kommer fra klienten, ikke broen. */
export function settFlagg(t: Lagertilstand, kontaktId: string, flagg: Operatorflagg): Lagertilstand {
  return motor.settFlagg(t, kontaktId, flagg);
}

/** Oppslag sensorId → art, som `trussel.ts` og `fusjon.ts` trenger. */
export function artOppslag(t: Lagertilstand): (sensorId: string) => Sensorart | undefined {
  return motor.artOppslag(t);
}
